import type { VariantProps } from 'class-variance-authority';
import {
	BodyS,
	cn,
	Skeleton,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@lqa-frontend/ui-system';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { useSidebar } from './SidebarProvider';
import { SidebarMenuSubButtonSize, SidebarState } from './types';
import type { ICssVariableStyle, ISidebarMenuSubButtonProps } from './types';

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot="sidebar-menu"
			data-sidebar="menu"
			className={cn('flex w-full min-w-0 flex-col gap-6', className)}
			{...props}
		/>
	);
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot="sidebar-menu-item"
			data-sidebar="menu-item"
			className={cn('group/menu-item relative', className)}
			{...props}
		/>
	);
}

const sidebarMenuButtonVariants = cva(
	'peer/menu-button flex w-full items-center gap-8 overflow-hidden rounded-s px-16 py-8 text-left text-14 outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-32 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>p:last-child]:truncate [&>svg]:size-18 [&>svg]:shrink-0 [&>svg]:text-sidebar-foreground/80',
	{
		variants: {
			variant: {
				default:
					' text-sidebar-accent-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
				active:
					'bg-sidebar-primary text-sidebar-accent-foreground font-semibold',
				outline:
					'bg-base-1 shadow-[0_0_0_1px_var(--color-sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--color-sidebar-accent)]',
			},
			size: {
				default: 'h-36 text-14',
				sm: 'h-28 text-12',
				lg: 'h-48 text-16 group-data-[collapsible=icon]:p-0!',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	}
);

function SidebarMenuButton({
	asChild = false,
	isActive = false,
	variant = 'default',
	size = 'default',
	tooltip,
	className,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean;
	isActive?: boolean;
	tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
	const Comp = asChild ? Slot : 'button';
	const { isMobile, state } = useSidebar();

	const button = (
		<Comp
			data-slot="sidebar-menu-button"
			data-sidebar="menu-button"
			data-size={size}
			data-active={isActive}
			className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
			{...props}
		/>
	);

	if (!tooltip) {
		return button;
	}

	if (typeof tooltip === 'string') {
		tooltip = {
			children: tooltip,
		};
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent
				side="right"
				align="center"
				hidden={state !== SidebarState.Collapsed || isMobile}
				{...tooltip}
			/>
		</Tooltip>
	);
}

function SidebarMenuAction({
	className,
	asChild = false,
	shouldShowOnHover = false,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean;
	shouldShowOnHover?: boolean;
}) {
	const Comp = asChild ? Slot : 'button';

	return (
		<Comp
			data-slot="sidebar-menu-action"
			data-sidebar="menu-action"
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-6 right-4 flex aspect-square w-20 items-center justify-center rounded-s p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-16 [&>svg]:shrink-0',
				// Increases the hit area of the button on mobile.
				'after:absolute after:-inset-8 md:after:hidden',
				'peer-data-[size=sm]/menu-button:top-4',
				'peer-data-[size=default]/menu-button:top-6',
				'peer-data-[size=lg]/menu-button:top-11',
				'group-data-[collapsible=icon]:hidden',
				shouldShowOnHover &&
					'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
				className
			)}
			{...props}
		/>
	);
}

function SidebarMenuBadge({
	className,
	children,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="sidebar-menu-badge"
			data-sidebar="menu-badge"
			className={cn(
				'text-sidebar-foreground pointer-events-none absolute right-4 flex h-20 min-w-20 items-center justify-center rounded-s px-4 tabular-nums select-none',
				'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
				'peer-data-[size=sm]/menu-button:top-4',
				'peer-data-[size=default]/menu-button:top-6',
				'peer-data-[size=lg]/menu-button:top-11',
				'group-data-[collapsible=icon]:hidden',
				className
			)}
			{...props}
		>
			<BodyS fontWeight="medium" className="text-inherit">
				{children}
			</BodyS>
		</div>
	);
}

function SidebarMenuSkeleton({
	className,
	shouldShowIcon = false,
	...props
}: React.ComponentProps<'div'> & {
	shouldShowIcon?: boolean;
}) {
	// Random width between 50 to 90%.
	const width = React.useMemo(() => {
		const randomValue = crypto.getRandomValues(new Uint32Array(1))[0];

		return `${50 + Math.floor((randomValue / 2 ** 32) * 40)}%`;
	}, []);

	const skeletonStyle: ICssVariableStyle = {
		'--skeleton-width': width,
	};

	return (
		<div
			data-slot="sidebar-menu-skeleton"
			data-sidebar="menu-skeleton"
			className={cn('flex h-32 items-center gap-8 rounded-s px-8', className)}
			{...props}
		>
			{shouldShowIcon && (
				<Skeleton
					className="size-16 rounded-s"
					data-sidebar="menu-skeleton-icon"
				/>
			)}
			<Skeleton
				className="h-16 max-w-(--skeleton-width) flex-1"
				data-sidebar="menu-skeleton-text"
				style={skeletonStyle}
			/>
		</div>
	);
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot="sidebar-menu-sub"
			data-sidebar="menu-sub"
			className={cn(
				'flex min-w-0 flex-col gap-4 py-2',
				'group-data-[collapsible=icon]:hidden',
				className
			)}
			{...props}
		/>
	);
}

function SidebarMenuSubItem({
	className,
	...props
}: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot="sidebar-menu-sub-item"
			data-sidebar="menu-sub-item"
			className={cn('group/menu-sub-item relative', className)}
			{...props}
		/>
	);
}

function SidebarMenuSubButton({
	asChild = false,
	size = SidebarMenuSubButtonSize.Medium,
	isActive = false,
	className,
	...props
}: ISidebarMenuSubButtonProps) {
	const Comp = asChild ? Slot : 'a';

	return (
		<Comp
			data-slot="sidebar-menu-sub-button"
			data-sidebar="menu-sub-button"
			data-size={size}
			data-active={isActive}
			className={cn(
				'text-sidebar-accent-foreground/75 ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-28 min-w-0 items-center gap-8 overflow-hidden rounded-s pl-42 pr-16 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-16 [&>svg]:shrink-0',
				'data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-accent-foreground',
				size === SidebarMenuSubButtonSize.Small && 'text-12',
				size === SidebarMenuSubButtonSize.Medium && 'text-14',
				'group-data-[collapsible=icon]:hidden',
				className
			)}
			{...props}
		/>
	);
}

export {
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuSkeleton,
	SidebarMenuSub,
	SidebarMenuSubItem,
	SidebarMenuSubButton,
};
